import { useEffect, useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import { fetchDogs } from '../lib/api';
import { saveDog, deleteDog, setVisible } from '../lib/adminApi';
import { ImageField, MultiImageField } from './components/ImageField';

const CATEGORIES = [
  { value: 'females', label: 'Suki' },
  { value: 'males', label: 'Psy (samce)' },
  { value: 'memory', label: 'Na zawsze z nami' },
];

const slugify = (s) =>
  (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const empty = {
  id: '', name: '', fullName: '', birthDate: '', category: 'females', origin: '',
  descriptionPl: '', descriptionEn: '', profileImage: null, pedigreeImage: null, images: [],
};

const DogsManagementPage = () => {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // dog object or null
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setDogs(await fetchDogs({ includeHidden: true })); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleVisible = async (d) => {
    try { await setVisible('dogs', d.id, d.visible === false); await load(); }
    catch (err) { alert(err.message); }
  };

  const openNew = () => { setEditing({ ...empty }); setIsNew(true); };
  const openEdit = (d) => {
    setEditing({
      id: d.id, name: d.name, fullName: d.fullName || '', birthDate: d.birthDate || '',
      category: d.category || 'females', origin: d.origin || '',
      descriptionPl: d.description?.pl || '', descriptionEn: d.description?.en || '',
      profileImage: d.profileImage || null, pedigreeImage: d.pedigreeImage || null, images: d.images || [],
    });
    setIsNew(false);
  };

  const handleSave = async () => {
    const dog = { ...editing };
    if (!dog.name.trim()) { alert('Podaj imię psa'); return; }
    if (isNew && !dog.id.trim()) dog.id = slugify(dog.name);
    if (!dog.id.trim()) { alert('Podaj identyfikator (slug)'); return; }
    setSaving(true);
    try {
      await saveDog(dog);
      setEditing(null);
      await load();
    } catch (err) { alert('Błąd zapisu: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Usunąć psa „${d.name}"? Tej operacji nie można cofnąć.`)) return;
    try { await deleteDog(d.id); await load(); }
    catch (err) { alert('Błąd usuwania: ' + err.message); }
  };

  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">Łącznie psów: <span className="font-semibold">{dogs.length}</span></p>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-accent text-white px-4 py-2 rounded-lg hover:opacity-90">
          <FaPlus /> Dodaj psa
        </button>
      </div>

      {loading ? (
        <p className="text-gray-500">Ładowanie...</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y">
          {dogs.map((d) => {
            const hidden = d.visible === false;
            return (
            <div key={d.id} className={`flex items-center gap-4 p-4 ${hidden ? 'opacity-50' : ''}`}>
              <img src={d.profileImage} alt={d.name} className="w-14 h-14 rounded-full object-cover bg-gray-100" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800">
                  {d.name} <span className="text-gray-400 font-normal">/ {d.fullName}</span>
                  {hidden && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full align-middle">ukryty</span>}
                </p>
                <p className="text-sm text-gray-500">
                  {CATEGORIES.find((c) => c.value === d.category)?.label} · {d.images?.length || 0} zdjęć{d.birthDate ? ` · ${d.birthDate}` : ''}
                </p>
              </div>
              <button onClick={() => toggleVisible(d)} className={`p-2 ${hidden ? 'text-gray-400 hover:text-green-600' : 'text-gray-500 hover:text-brand-accent'}`} title={hidden ? 'Pokaż na stronie' : 'Ukryj na stronie'}>
                {hidden ? <FaEyeSlash /> : <FaEye />}
              </button>
              <button onClick={() => openEdit(d)} className="p-2 text-gray-500 hover:text-brand-accent" title="Edytuj"><FaEdit /></button>
              <button onClick={() => handleDelete(d)} className="p-2 text-gray-500 hover:text-red-600" title="Usuń"><FaTrash /></button>
            </div>
            );
          })}
          {dogs.length === 0 && <p className="p-6 text-gray-500">Brak psów. Dodaj pierwszego.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">{isNew ? 'Nowy pies' : `Edycja: ${editing.name}`}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Imię *</label>
                  <input value={editing.name} onChange={(e) => set('name', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pełne imię (rodowodowe)</label>
                  <input value={editing.fullName} onChange={(e) => set('fullName', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Identyfikator (slug)</label>
                  <input value={editing.id} onChange={(e) => set('id', e.target.value)} disabled={!isNew} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100" placeholder="np. gina" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data urodzenia</label>
                  <input value={editing.birthDate} onChange={(e) => set('birthDate', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="01.06.2020" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria</label>
                  <select value={editing.category} onChange={(e) => set('category', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pochodzenie</label>
                  <input value={editing.origin} onChange={(e) => set('origin', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Import Rosja" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <ImageField label="Zdjęcie profilowe" value={editing.profileImage} onChange={(v) => set('profileImage', v)} folder="dogs" />
                <ImageField label="Rodowód (zdjęcie)" value={editing.pedigreeImage} onChange={(v) => set('pedigreeImage', v)} folder="dogs" />
              </div>

              <MultiImageField label="Galeria zdjęć" value={editing.images} onChange={(v) => set('images', v)} folder="dogs" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis (PL)</label>
                  <textarea value={editing.descriptionPl} onChange={(e) => set('descriptionPl', e.target.value)} rows={6} className="w-full border border-gray-300 rounded-md px-3 py-2 whitespace-pre-wrap" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis (EN)</label>
                  <textarea value={editing.descriptionEn} onChange={(e) => set('descriptionEn', e.target.value)} rows={6} className="w-full border border-gray-300 rounded-md px-3 py-2 whitespace-pre-wrap" />
                </div>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Anuluj</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-md bg-brand-accent text-white hover:opacity-90 disabled:opacity-50">
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DogsManagementPage;
